import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, Row, Col, Typography, Statistic, Table, Spin, message } from 'antd'
import {
  VideoCameraOutlined,
  AudioOutlined,
  BookOutlined,
  UserOutlined,
  HeartOutlined,
  EyeOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { Breadcrumbs } from '../../components'
import { selectAllVideos } from '../../redux/features/videos/videoSelectors'
import { selectAllAudios } from '../../redux/features/audio/audioSelector'
import { selectAllBooks } from '../../redux/features/books/bookSelector'
import { selectAllUsers } from '../../redux/features/user/userSelectors'

const { Title, Text } = Typography

interface ContentStats {
  title: string
  likes: number
  category: string
  type: string
}

const Analytics: React.FC = () => {
  const videos = useSelector(selectAllVideos)
  const audios = useSelector(selectAllAudios)
  const books = useSelector(selectAllBooks)
  const users = useSelector(selectAllUsers) || []

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Simulate loading time for data to be fetched
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Calculate total likes
  const totalVideoLikes = videos.reduce((sum, v) => sum + (v.likes || 0), 0)
  const totalAudioLikes = audios.reduce((sum, a) => sum + (a.likes || 0), 0)
  const totalBookLikes = books.reduce((sum, b) => sum + (b.likes || 0), 0)
  const totalLikes = totalVideoLikes + totalAudioLikes + totalBookLikes

  // Get categories breakdown
  const videoCategories = [...new Set(videos.map(v => v.category).filter(Boolean))]
  const audioCategories = [...new Set(audios.map(a => a.category).filter(Boolean))]
  const bookCategories = [...new Set(books.map(b => b.category).filter(Boolean))]

  // Top content by likes
  const allContent: ContentStats[] = [
    ...videos.map(v => ({ title: v.title, likes: v.likes || 0, category: v.category, type: 'Video' })),
    ...audios.map(a => ({ title: a.title, likes: a.likes || 0, category: a.category, type: 'Audio' })),
    ...books.map(b => ({ title: b.title, likes: b.likes || 0, category: b.category, type: 'Book' })),
  ].sort((a, b) => b.likes - a.likes).slice(0, 10)

  // Users with subscriptions
  const subscribedUsers = users.filter((u: any) => u.isSubscribed)

  const topContentColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Likes',
      dataIndex: 'likes',
      key: 'likes',
      render: (likes: number) => (
        <span>
          <HeartOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          {likes}
        </span>
      ),
    },
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumbs />
      <div style={{ padding: '20px' }}>
        <Title level={4}>Content Analytics</Title>

        {/* Overview Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Content"
                value={videos.length + audios.length + books.length}
                prefix={<LineChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Likes"
                value={totalLikes}
                prefix={<HeartOutlined style={{ color: '#ff4d4f' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={users.length}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Subscribed Users"
                value={subscribedUsers.length}
                prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Content Breakdown */}
        <Title level={5}>Content Breakdown</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Videos"
                value={videos.length}
                prefix={<VideoCameraOutlined style={{ color: '#1890ff' }} />}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({videoCategories.length} categories)
                  </Text>
                }
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Total likes: {totalVideoLikes}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Audio Podcasts"
                value={audios.length}
                prefix={<AudioOutlined style={{ color: '#722ed1' }} />}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({audioCategories.length} categories)
                  </Text>
                }
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Total likes: {totalAudioLikes}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Books"
                value={books.length}
                prefix={<BookOutlined style={{ color: '#fa8c16' }} />}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({bookCategories.length} categories)
                  </Text>
                }
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Total likes: {totalBookLikes}</Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Top Content */}
        <Title level={5}>Top Content by Likes</Title>
        <Card>
          <Table
            dataSource={allContent}
            columns={topContentColumns}
            rowKey={(record, index) => `${record.title}-${index}`}
            pagination={false}
            size="small"
          />
        </Card>
      </div>
    </div>
  )
}

export default Analytics
